import useMdBreakpoint from "@/hooks/useMdBreakpoint";
import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";

const MultilinePlaceholderInput = ({
  value,
  onChange,
  placeholder,
  withBorder,
  textareaRef,
  placeholderRef,
}: {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  placeholder: string;
  withBorder: boolean;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  placeholderRef: React.RefObject<HTMLDivElement | null>;
}) => {
  const showFakePlaceholder = !value; // only depends on value

  return (
    <div className="relative w-full py-4">
      {/* real textarea */}
      <textarea
        ref={textareaRef}
        rows={1} // base: 1 line
        className={`absolute w-full text-input resize-none overflow-hidden bg-transparent focus:outline-none box-border ${withBorder ? "text-border-input" : ""
          }`}
        style={{ maxHeight: "96px" }} // 4 lines * 24px (example)
        value={value}
        onChange={onChange}
      />

      {/* fake placeholder */}
      <div
        ref={placeholderRef}
        className={`text-white/60 cursor-text z-0 whitespace-pre-wrap line-clamp-4 ${showFakePlaceholder ? "" : "invisible"
          }`}
        style={{ maxHeight: "96px" }}
        onClick={() => {
          textareaRef.current?.focus();
        }}
      >
        {placeholder}
      </div>
    </div>
  );
};

interface TextInputProps {
  placeholder?: string;
  value?: string;
  onChange: (text: string) => void;
  onSubmit?: () => void;
  withBorder?: boolean;
}

const TextInput: React.FC<TextInputProps> = ({
  placeholder = "",
  withBorder = true,
  value,
  onChange,
  onSubmit,
}) => {
  const [inputValue, setInputValue] = useState("");
  const { t } = useTranslation();
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const isMd = useMdBreakpoint();
  const placeholderRef = useRef<HTMLDivElement>(null);

  // the auto-grow height is set on change, reset it when the value is cleared
  useEffect(() => {
    if (value !== "") return;

    if (inputRef.current) {
      inputRef.current.style.height = "auto";
    }
    if (placeholderRef.current) {
      placeholderRef.current.style.height = "auto";
    }
  }, [value]);

  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;

    const scrollInputIntoView = () => {
      if (!input) return;

      requestAnimationFrame(() => {
        input.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });

        if (window.visualViewport) {
          const viewport = window.visualViewport;
          const inputRect = input.getBoundingClientRect();
          const viewportHeight = viewport.height;
          const viewportTop = viewport.offsetTop;

          // Calculate if input is visible in the visual viewport
          const inputTop = inputRect.top - viewportTop;
          const inputBottom = inputRect.bottom - viewportTop;

          // If input is below or above the visible viewport, scroll it into view
          if (inputBottom > viewportHeight || inputTop < 0) {
            const scrollOffset = inputBottom - viewportHeight + 100;
            if (scrollOffset > 0) {
              window.scrollBy({
                top: scrollOffset,
                behavior: "smooth",
              });
            }
          }
        } else {
          // Fallback for browsers without visualViewport API
          const inputRect = input.getBoundingClientRect();
          const windowHeight = window.innerHeight;

          // Check if input is in the lower half of the screen (where keyboard would cover it)
          if (inputRect.bottom > windowHeight * 0.5) {
            const scrollOffset = inputRect.bottom - windowHeight * 0.3; // Position in upper 30% of screen
            window.scrollBy({
              top: scrollOffset,
              behavior: "smooth",
            });
          }
        }
      });
    };

    const handleFocus = () => {
      if (!isMd) {
        setTimeout(scrollInputIntoView, 300);
      }
    };

    const handleTouchStart = () => {
      if (!isMd) {
        setTimeout(scrollInputIntoView, 100);
      }
    };

    input.addEventListener("focus", handleFocus);
    input.addEventListener("touchstart", handleTouchStart);

    return () => {
      input.removeEventListener("focus", handleFocus);
      input.removeEventListener("touchstart", handleTouchStart);
    };
  }, [isMd]);

  return (
    <form
      className="w-full"
      onSubmit={(e) => {
        e.preventDefault();
        if (onSubmit) {
          onSubmit();
          setInputValue("")
          inputRef.current?.blur()
        }
      }}
    >
      <MultilinePlaceholderInput
        value={value !== undefined ? value : inputValue}
        onChange={(e) => {
          const text = e.target.value;
          setInputValue(text);

          const textarea = inputRef.current;
          const placeholder = placeholderRef.current;

          if (textarea && placeholder) {
            // Reset textarea height to auto to allow it to shrink or grow
            textarea.style.height = "auto";
            const newTextareaHeight = `${Math.min(
              textarea.scrollHeight,
              96
            )}px`;
            textarea.style.height = newTextareaHeight;

            // If text exists, sync placeholder height. If not, reset it to auto.
            // This allows the placeholder to correctly show 4 lines when input is cleared.
            placeholder.style.height = text ? newTextareaHeight : "auto";
          }

          onChange(text);
        }}
        placeholder={t(placeholder)}
        withBorder={withBorder}
        textareaRef={inputRef}
        placeholderRef={placeholderRef}
      />
    </form>
  );
};

export default TextInput;
